import { IHTVPlatform } from './platform'
import { LocalChannel } from '../interfaces/local-channel'
import { Injectable } from '@angular/core'
import { ChannelPosition } from '../pages/channel/channel-position'
import { MonitoringMemoryData, MonitoringStorageData, SystemData } from '../interfaces/monitoring'
import { LocalMediaFile, Media } from 'htv-lib'
import { LocalAppLaunchMode, TVApp } from '../interfaces/local-app'
import { ChannelTrackData, ChannelTrackDataFlags, CurrentChannelData } from '../interfaces/channel-track-data'
import { PowerState } from '../interfaces/power-state'
import { BehaviorSubject } from 'rxjs'
import { LogService } from 'htv-lib'
import { NetworkConnectionType, NetworkData, NetworkIpMode, NetworkState } from '../interfaces/network'
import { StatusSuccessDto } from '../interfaces/dto/status-response-dto'
import { RouterService, RouterPage } from '../services/router.service'
import { GoogleCastState } from '../interfaces/local-googlecast'
import { ExternalInputData } from '../services/external-input.service'
import { VODVideo } from '../interfaces/vod'

@Injectable({
  providedIn: 'root',
})
export class DefaultPlatform implements IHTVPlatform {
  audioTracks: ChannelTrackData[] = [
    { index: 0, lang: 'fr', data: {}, flags: [] },
    { index: 1, lang: 'en', data: {}, flags: [] },
  ]
  subtitleTracks: ChannelTrackData[] = [
    { index: 3, lang: 'fr', data: {}, flags: [] },
    { index: 4, lang: 'fr', data: {}, flags: [ChannelTrackDataFlags.HEARING_IMPAIRED] },
    { index: 5, lang: 'en', data: {}, flags: [] },
    { index: 6, lang: 'it', data: {}, flags: [] },
    { index: 7, lang: 'de', data: {}, flags: [ChannelTrackDataFlags.HEARING_IMPAIRED] },
    { index: 8, lang: 'es', data: {}, flags: [] },
  ]
  isSubtitleEnabled = false
  currentAudioTrack: ChannelTrackData = this.audioTracks[0]
  currentSubtitleTrack: ChannelTrackData = this.subtitleTracks[0]

  muted = false
  volume = 10

  powerState?: PowerState
  pendingPowerState?: PowerState
  powerStateTimer: any
  readonly powerStateTimerDelayInMs = 1000

  gcInitializedSubject?: BehaviorSubject<boolean | undefined>

  constructor(
    private logService: LogService,
    private routerService: RouterService
  ) {
    this.logService.debug('DefaultPlatform loaded')
  }

  id(): string {
    return 'DefaultPlatform'
  }

  init(): Promise<void> {
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  afterRegistrationHook(deviceId: string | undefined): void {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  afterStatusHook(status: StatusSuccessDto): Promise<void> {
    return Promise.resolve()
  }

  enableGuestMode(): Promise<void> {
    return Promise.resolve()
  }

  disableGuestMode(): Promise<void> {
    return Promise.resolve()
  }

  clearData(): Promise<void> {
    return Promise.resolve()
  }

  getSystemData(): Promise<SystemData> {
    return Promise.resolve({
      model: 'Modelf',
      manufacturer: 'Manufacturer',
      serialNumber: 'SerialNumber',
      firmwareVersion: 'FirmwareVersion',
      appVersion: 'AppVersion',
    })
  }

  getPlatformData(): Promise<object> {
    return Promise.resolve({
      userAgent: navigator.userAgent,
    })
  }

  getCpuUsage(): Promise<number> {
    return Promise.resolve(0)
  }

  getMemoryData(): Promise<MonitoringMemoryData> {
    return Promise.resolve({
      availableMemory: 0,
      totalMemory: 0,
    })
  }

  getStorageData(): Promise<MonitoringStorageData[]> {
    return Promise.resolve([])
  }

  getTemperature(): Promise<number> {
    return Promise.resolve(0)
  }

  getValue(key: string): any {
    return localStorage.getItem(key)
  }

  setValue(key: string, value: any): void {
    localStorage.setItem(key, value)
  }

  removeValue(key: string): void {
    localStorage.removeItem(key)
  }

  clearValues(): void {
    localStorage.clear()
  }

  reboot(): void {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setLanguage(code: string): Promise<void> {
    return Promise.resolve()
  }

  getPowerState(): Promise<PowerState | undefined> {
    return Promise.resolve(this.pendingPowerState)
  }

  setPowerState(state: PowerState): void {
    this.pendingPowerState = state
  }

  registerPowerStateListener(subject: BehaviorSubject<PowerState | undefined>): void {
    if (this.powerStateTimer) {
      clearInterval(this.powerStateTimer)
    }
    this.powerStateTimer = setInterval(async () => {
      const powerState = await this.getPowerState()
      if (this.powerState != powerState) {
        this.powerState = powerState
        subject.next(powerState)
      }
    }, this.powerStateTimerDelayInMs)
  }

  getNetworkState(): Promise<NetworkState> {
    return Promise.resolve(NetworkState.CONNECTED)
  }

  getNetworkData(): Promise<NetworkData> {
    return Promise.resolve({
      type: NetworkConnectionType.UNKNOWN,
      macAddress: '',
      ipAddress: '',
      ipMode: NetworkIpMode.UNKNOWN,
      subnetMask: '',
      gateway: '',
      primaryDns: '',
      secondaryDns: '',
      proxy: '',
      wifi: undefined,
      softAP: undefined,
    })
  }

  registerNetworkStateListener(subject: BehaviorSubject<NetworkState>): void {
    subject.next(NetworkState.CONNECTED)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerApplicationInstalledListener(subject: BehaviorSubject<string[]>): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerApplicationFocusListener(subject: BehaviorSubject<boolean>): void {
    // do nothing
  }

  getAppList(): Promise<TVApp[]> {
    return Promise.resolve([])
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startApp(appId: string): void {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startNetflixApp(appId: string, hotelId: string, launchMode: LocalAppLaunchMode): void {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerChannelListener(subject: BehaviorSubject<boolean>): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startUDPChannel(channel: LocalChannel, pos: ChannelPosition): void {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startWebChannel(channel: LocalChannel, pos: ChannelPosition): void {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  startBroadcastChannel(channel: LocalChannel, pos: ChannelPosition): void {
    return
  }

  stopUDPChannel(): void {
    return
  }

  stopWebChannel(): void {
    return
  }

  stopBroadcastChannel(): void {
    return
  }

  getCurrentChannel(): Promise<any> {
    return Promise.resolve()
  }

  getUDPCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    const channelData: CurrentChannelData = {
      audioTracks: this.audioTracks,
      subtitleTracks: this.subtitleTracks,
      isSubtitleEnabled: this.isSubtitleEnabled,
      currentAudioTrack: this.currentAudioTrack,
      currentSubtitleTrack: this.currentSubtitleTrack,
    }
    return Promise.resolve(channelData)
  }

  getWebCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    const channelData: CurrentChannelData = {
      audioTracks: this.audioTracks,
      subtitleTracks: this.subtitleTracks,
      isSubtitleEnabled: this.isSubtitleEnabled,
      currentAudioTrack: this.currentAudioTrack,
      currentSubtitleTrack: this.currentSubtitleTrack,
    }
    return Promise.resolve(channelData)
  }

  getBroadcastCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    const channelData: CurrentChannelData = {
      audioTracks: this.audioTracks,
      subtitleTracks: this.subtitleTracks,
      isSubtitleEnabled: this.isSubtitleEnabled,
      currentAudioTrack: this.currentAudioTrack,
      currentSubtitleTrack: this.currentSubtitleTrack,
    }
    return Promise.resolve(channelData)
  }

  setUDPAudioTrack(track: ChannelTrackData): void {
    this.currentAudioTrack = track
    return
  }

  setWebAudioTrack(track: ChannelTrackData): void {
    this.currentAudioTrack = track
    return
  }

  setBroadcastAudioTrack(track: ChannelTrackData): void {
    this.currentAudioTrack = track
    return
  }

  setUDPSubtitleTrack(track: ChannelTrackData): void {
    this.currentSubtitleTrack = track
    return
  }

  setWebSubtitleTrack(track: ChannelTrackData): void {
    this.currentSubtitleTrack = track
    return
  }

  setBroadcastSubtitleTrack(track: ChannelTrackData): void {
    this.currentSubtitleTrack = track
    return
  }

  setSubtitleState(enable: boolean): Promise<boolean> {
    this.isSubtitleEnabled = enable
    return Promise.resolve(enable)
  }

  getLocalMedias(): Promise<LocalMediaFile[]> {
    return Promise.resolve([])
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  downloadMedia(media: Media): Promise<LocalMediaFile> {
    return Promise.reject(new Error('Not supported'))
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeMedia(mediaFile: LocalMediaFile): Promise<void> {
    return Promise.resolve()
  }

  getVolume(): Promise<number> {
    return Promise.resolve(this.volume)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setNativeVolumeDisplay(enabled: boolean): void {
    return
  }

  setVolume(volume: number): Promise<number> {
    this.volume = volume
    return Promise.resolve(this.volume)
  }

  setVolumeDown(): Promise<number> {
    return Promise.resolve(this.volume--)
  }

  setVolumeUp(): Promise<number> {
    return Promise.resolve(this.volume++)
  }

  getVolumeMute(): Promise<boolean> {
    return Promise.resolve(this.muted)
  }

  setVolumeMute(muted: boolean): Promise<boolean> {
    this.muted = muted
    return Promise.resolve(this.muted)
  }

  getExternalInputs(): Promise<ExternalInputData[]> {
    return Promise.resolve([])
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  registerExternalInputListener(subject: BehaviorSubject<ExternalInputData[]>): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showHDMI(externalInput: ExternalInputData): Promise<void> {
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hideHDMI(): Promise<void> {
    return Promise.resolve()
  }

  hasIntegratedGoogleCast(): boolean {
    return true
  }

  initGoogleCastApp(): void {
    this.gcInitializedSubject?.next(true)
  }

  registerGoogleCastInitializedListener(subject: BehaviorSubject<boolean | undefined>): void {
    this.gcInitializedSubject = subject
  }

  disableGoogleCast(): Promise<void> {
    return Promise.resolve()
  }

  initGoogleCastProxy(): Promise<void> {
    return Promise.resolve()
  }

  initGoogleCastSoftAP(): Promise<void> {
    return Promise.resolve()
  }

  acceptGoogleCastTermsOfService(): Promise<void> {
    return Promise.resolve()
  }

  enableGoogleCastUsageReporting(): Promise<void> {
    return Promise.resolve()
  }

  clearGoogleCastData(): Promise<void> {
    return Promise.resolve()
  }

  openGoogleCastLocalPage(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    roomNumber: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    supportMessage: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    gcStateSubject: BehaviorSubject<GoogleCastState | undefined>
  ): Promise<void> {
    this.routerService.goToPage(RouterPage.CAST)
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  openGoogleCastSoftAPPage(roomNumber: string, pairingStatus: boolean, supportMessage: string): Promise<void> {
    this.routerService.goToPage(RouterPage.CAST)
    return Promise.resolve()
  }

  pollGoogleCastSoftAP(): Promise<any> {
    return Promise.resolve()
  }

  launchGoogleCastSettings(): Promise<void> {
    return Promise.resolve()
  }

  adminOpenAppsSettings(): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  downloadAndUpgradeApp(deviceId: string): void {
    // do nothing
  }

  canDownloadAndUpgradeApp(): boolean {
    return false
  }

  hasCustomVOD(): boolean {
    return false
  }

  getShakaPlatformConfig(): any {
    return undefined
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodStart(vod: VODVideo, pos: ChannelPosition): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodPlay(vod: VODVideo): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodPause(vod: VODVideo): void {
    // do nothing
  }

  vodStop(): void {
    // do nothing
  }

  vodGetCurrentTime(): number {
    return 0
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodSpeed(vod: VODVideo, speed: number): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodSeekTo(vod: VODVideo, milliseconds: number): Promise<void> {
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodJumpBackward(vod: VODVideo, milliseconds: number): Promise<void> {
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodJumpForward(vod: VODVideo, milliseconds: number): Promise<void> {
    return Promise.resolve()
  }

  initVirtualKeyboard(): void {
    return
  }
}
